import { create } from 'zustand';
import { CartState, ServiceType, Tire } from '../types';
import { cartPersistenceService } from '../services/cartPersistence';

type CartStore = CartState & {
  addToCart: (tire: Tire, quantity: number) => void;
  removeFromCart: (tireId: string) => void;
  updateQuantity: (tireId: string, quantity: number) => void;
  setServiceType: (type: ServiceType) => void;
  setLocation: (locationId: string) => void;
  clearCart: () => void;
  calculateTotal: () => void;
  loadPersistedCart: () => void;
  saveCartToStorage: () => void;
  mergeGuestCart: () => void;
};

const useCartStore = create<CartStore>((set, get) => ({
  items: [],
  service_type: ServiceType.FITTING,
  selected_location_id: null,
  total: 0,
    addToCart: (tire: Tire, quantity: number) => {
    const { items } = get();
    const existingItemIndex = items.findIndex(item => item.tire.id === tire.id);
    
    if (existingItemIndex !== -1) {
      const updatedItems = [...items];
      updatedItems[existingItemIndex].quantity += quantity;
      set({ items: updatedItems });
    } else {
      set({ items: [...items, { tire, quantity }] });
    }
    
    get().calculateTotal();
    get().saveCartToStorage();
  },
    removeFromCart: (tireId: string) => {
    const { items } = get();
    const updatedItems = items.filter(item => item.tire.id !== tireId);
    set({ items: updatedItems });
    get().calculateTotal();
    get().saveCartToStorage();
  },
    updateQuantity: (tireId: string, quantity: number) => {
    const { items } = get();
    const updatedItems = items.map(item => 
      item.tire.id === tireId ? { ...item, quantity } : item
    );
    set({ items: updatedItems });
    get().calculateTotal();
    get().saveCartToStorage();
  },
    setServiceType: (type: ServiceType) => {
    // When changing service type, reset the selected location
    set({ 
      service_type: type,
      selected_location_id: null
    });
    get().calculateTotal();
    get().saveCartToStorage();
  },
    setLocation: (locationId: string) => {
    set({ selected_location_id: locationId });
    get().saveCartToStorage();
  },
    clearCart: () => {
    set({ items: [], total: 0 });
    cartPersistenceService.clearCart();
  },
    calculateTotal: () => {
    const { items } = get();
    let total = 0;
    
    // Calculate subtotal from items (no additional service fees)
    items.forEach(item => {
      const price = item.tire.salePrice || item.tire.price;
      total += price * item.quantity;
    });
    
    // No fitting fees - all fitting services are free
    
    set({ total });
  },

  /**
   * Load cart data from localStorage
   */
  loadPersistedCart: () => {
    const persistedCart = cartPersistenceService.loadCart();
    if (persistedCart) {
      console.log('Loading persisted cart:', cartPersistenceService.getCartSummary());
      set({
        items: persistedCart.items,
        service_type: persistedCart.service_type,
        selected_location_id: persistedCart.selected_location_id,
        total: persistedCart.total
      });
      // Recalculate total to ensure accuracy
      get().calculateTotal();
    }
  },

  /**
   * Save current cart state to localStorage
   */
  saveCartToStorage: () => {
    const state = get();
    cartPersistenceService.saveCart({
      items: state.items,
      service_type: state.service_type,
      selected_location_id: state.selected_location_id,
      total: state.total
    });
  },

  /**
   * Merge guest cart when user signs in
   * This should be called after authentication is established
   */
  mergeGuestCart: () => {
    const currentState = get();
    const persistedGuestCart = cartPersistenceService.loadCart();
    
    if (!persistedGuestCart || persistedGuestCart.items.length === 0) {
      console.log('No guest cart to merge');
      return;
    }

    // If current cart is empty, just load the persisted cart
    if (currentState.items.length === 0) {
      console.log('Current cart empty, loading persisted guest cart');
      get().loadPersistedCart();
      return;
    }

    // Merge guest cart with current cart
    const currentCartData = {
      items: currentState.items,
      service_type: currentState.service_type,
      selected_location_id: currentState.selected_location_id,
      total: currentState.total,
      lastUpdated: Date.now()
    };

    const mergedCart = cartPersistenceService.mergeGuestCartWithUserCart(
      persistedGuestCart,
      currentCartData
    );

    console.log('Merging guest cart with user cart:', {
      guestItems: persistedGuestCart.items.length,
      userItems: currentState.items.length,
      mergedItems: mergedCart.items.length
    });

    set({
      items: mergedCart.items,
      service_type: mergedCart.service_type,
      selected_location_id: mergedCart.selected_location_id
    });

    // Recalculate total and save merged cart
    get().calculateTotal();
    get().saveCartToStorage();
  }
}));

export default useCartStore;